import React, { useEffect, useState, useRef, useCallback } from "react";
import API from "../api/axiosConfig";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Percent, Heart, Clock,
  BadgeCheck, Flame, Tag, PackageSearch, ChevronDown, Eye,
} from "lucide-react";
import { useFavorites } from "../context/FavoritesContext";
import { toast } from "react-toastify";
import { isSedeOpen } from "../utils/sedeStatus";
import MobileBottomBar from "../components/MobileBottomBar";
import { getImageUrl } from "../utils/getImageUrl";
import ProductQuickViewModal from "../components/ProductQuickViewModal";
import CartQtyControl from "../components/CartQtyControl";

function normalize(obj) {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj.map(normalize);
  return { ...obj, _id: obj._id ?? obj.id };
}

const SORT_OPTIONS = [
  { value: "discount", label: "Mayor descuento" },
  { value: "price_asc", label: "Menor precio" },
  { value: "price_desc", label: "Mayor precio" },
  { value: "newest", label: "Más recientes" },
];

export default function OffersPage() {
  const [products, setProducts]     = useState([]);
  const [page, setPage]             = useState(1);
  const [hasMore, setHasMore]       = useState(true);
  const [loading, setLoading]       = useState(false);
  const [total, setTotal]           = useState(null);
  const [sort, setSort]             = useState("discount");
  const [showSort, setShowSort]     = useState(false);
  const bottomRef                   = useRef(null);
  const observerRef                 = useRef(null);
  const sortRef                     = useRef(null);

  const navigate = useNavigate();
  const { favorites, toggleFavorite } = useFavorites();
  const [quickView, setQuickView] = useState(null);

  const normalizeSedes = (product) => {
    if (Array.isArray(product.sedes)) return product.sedes;
    if (Array.isArray(product.productSedes))
      return product.productSedes.map((ps) => ps.sede ?? ps);
    return [];
  };

  const productIsOpen = (product) => {
    const sedes = normalizeSedes(product);
    if (!sedes.length) return false;
    return sedes.some((s) => isSedeOpen(s).open);
  };

  const getClosedMessage = (product) => {
    const sedes = normalizeSedes(product);
    if (!sedes.length) return "Cerrado";
    return isSedeOpen(sedes[0]).message || "Cerrado";
  };

  const fetchOffers = useCallback(async (p = 1, reset = false) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await API.get(`/products/offers?page=${p}&limit=20`);
      const raw   = res.data?.data?.products ?? res.data?.products ?? [];
      const items = (Array.isArray(raw) ? raw : []).map(normalize);
      setTotal(res.data?.data?.total ?? res.data?.total ?? null);
      const pages = res.data?.data?.pages ?? res.data?.pages;
      setHasMore(pages ? p < pages : items.length === 20);
      if (reset) {
        setProducts(items);
      } else {
        setProducts((prev) => {
          const ids = new Set(prev.map((x) => x._id));
          const merged = [...prev];
          for (const x of items) {
            if (!ids.has(x._id)) { merged.push(x); ids.add(x._id); }
          }
          return merged;
        });
      }
      setPage(p + 1);
    } catch {
      toast.error("Error al cargar ofertas");
    } finally {
      setLoading(false);
    }
  }, [loading]);

  // Initial load
  useEffect(() => {
    fetchOffers(1, true);
    // eslint-disable-next-line
  }, []);

  // Infinite scroll
  useEffect(() => {
    if (!bottomRef.current) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting && hasMore && !loading) fetchOffers(page); },
      { rootMargin: "200px", threshold: 0.1 }
    );
    observerRef.current.observe(bottomRef.current);
    return () => { if (observerRef.current) observerRef.current.disconnect(); };
    // eslint-disable-next-line
  }, [bottomRef.current, hasMore, loading, page]);

  // Close sort dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) setShowSort(false);
    };
    if (showSort) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showSort]);

  const sortedProducts = [...products].sort((a, b) => {
    if (sort === "discount") {
      const da = ((a.price - a.offerPrice) / a.price);
      const db = ((b.price - b.offerPrice) / b.price);
      return db - da;
    }
    if (sort === "price_asc")  return a.offerPrice - b.offerPrice;
    if (sort === "price_desc") return b.offerPrice - a.offerPrice;
    return 0; // newest — already sorted by backend
  });

  const currentSortLabel = SORT_OPTIONS.find((o) => o.value === sort)?.label;

  return (
    <div className="min-h-screen bg-[#f7f8f9] pb-28 lg:pb-8">

      {/* ── Hero header ─────────────────────────────────────────────────────── */}
      <div
        className="sticky top-0 z-30"
        style={{ background: "linear-gradient(135deg, #ff4500 0%, #ff6b35 50%, #ffa726 100%)" }}
      >
        {/* Nav row */}
        <div className="flex items-center gap-3 px-4 pt-4 pb-2">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition shrink-0"
          >
            <ArrowLeft size={18} className="text-white" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xl">🔥</span>
              <h1 className="text-white font-black text-lg leading-tight tracking-tight">
                Productos en oferta
              </h1>
            </div>
            {total != null && (
              <p className="text-white/75 text-[11px] font-medium pl-8">
                {total} producto{total !== 1 ? "s" : ""} con descuento
              </p>
            )}
          </div>

          {/* Sort button */}
          <div className="relative" ref={sortRef}>
            <button
              onClick={() => setShowSort(!showSort)}
              className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-[11px] font-bold px-3 py-2 rounded-2xl hover:bg-white/30 transition"
            >
              <Tag size={11} />
              {currentSortLabel}
              <ChevronDown size={11} className={`transition-transform ${showSort ? "rotate-180" : ""}`} />
            </button>
            {showSort && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 w-44 overflow-hidden">
                {SORT_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => { setSort(o.value); setShowSort(false); }}
                    className={`w-full text-left px-4 py-2.5 text-xs font-semibold transition
                      ${sort === o.value ? "bg-orange-50 text-orange-600" : "text-gray-700 hover:bg-gray-50"}`}
                  >
                    {sort === o.value && <span className="mr-1">✓</span>}
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Discount stats strip */}
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto no-scrollbar">
          {[10, 20, 30, 50].map((pct) => {
            const count = products.filter((p) =>
              Math.round(((p.price - p.offerPrice) / p.price) * 100) >= pct
            ).length;
            if (!count) return null;
            return (
              <div key={pct} className="flex-shrink-0 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl px-3 py-1.5 text-center">
                <p className="text-white font-black text-sm leading-none">{count}</p>
                <p className="text-white/80 text-[10px] font-medium">+{pct}% off</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Products grid ───────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 pt-4">
        <AnimatePresence mode="wait">
          {sortedProducts.length > 0 ? (
            <motion.div
              key="grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            >
              {sortedProducts.map((p, i) => {
                const isOpen    = productIsOpen(p);
                const closed    = !isOpen ? getClosedMessage(p) : null;
                const discount  = Math.round(((p.price - p.offerPrice) / p.price) * 100);
                const companyId = p.companyId ?? p.company?._id ?? p.company?.id;
                const isFav     = favorites.some((f) => f._id === p._id);

                return (
                  <motion.div
                    key={p._id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.3) }}
                    whileHover={{ y: -4, boxShadow: "0 12px 32px rgba(0,0,0,0.12)" }}
                    onClick={() => isOpen ? navigate(`/product/${p._id}`) : navigate(`/company/${companyId}`)}
                    className="bg-white rounded-2xl overflow-hidden cursor-pointer flex flex-col"
                    style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}
                  >
                    {/* Image */}
                    <div className="relative w-full h-36 bg-gray-50 shrink-0 overflow-hidden">
                      {!isOpen && <div className="absolute inset-0 bg-black/20 z-10" />}
                      {!isOpen && (
                        <div className="absolute top-2 left-2 z-20 bg-black/60 text-white text-[9px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                          <Clock size={8} /> {closed}
                        </div>
                      )}

                      {/* Discount badge — eye-catching */}
                      <div className="absolute top-2 right-2 z-20 flex items-center gap-0.5 bg-gradient-to-r from-orange-500 to-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow-lg">
                        <Percent size={8} /> -{discount}%
                      </div>

                      {p.images?.[0]
                        ? <img
                            src={getImageUrl(p.images[0])}
                            alt={p.title}
                            className="w-full h-full object-cover"
                          />
                        : <div className="w-full h-full flex items-center justify-center bg-orange-50">
                            <Flame size={32} className="text-orange-200" />
                          </div>
                      }

                      {/* Company gradient overlay */}
                      <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-black/50 to-transparent z-10" />
                      <p className="absolute bottom-1 left-2 right-2 text-[9px] text-white font-semibold truncate z-20 flex items-center gap-0.5">
                        {p.company?.name}
                        {p.company?.verified && <BadgeCheck size={8} className="text-blue-300 shrink-0" />}
                      </p>
                    </div>

                    {/* Info */}
                    <div className="p-2.5 flex flex-col flex-1">
                      <p className="text-[11px] font-bold text-gray-800 line-clamp-2 leading-snug mb-1.5">
                        {p.title}
                      </p>

                      <div className="flex items-baseline gap-1.5 mb-0.5">
                        <span className="text-[10px] text-gray-400 line-through">
                          ${Number(p.price).toLocaleString("es-CO")}
                        </span>
                        <span className="text-sm font-black text-supernova-600">
                          ${Number(p.offerPrice).toLocaleString("es-CO")}
                        </span>
                      </div>

                      <p className="text-[10px] text-green-600 font-bold mb-2">
                        Ahorras ${Number(p.price - p.offerPrice).toLocaleString("es-CO")}
                      </p>

                      <div className="flex items-center gap-1 mt-auto" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => {
                            toggleFavorite(p);
                            toast.info(
                              favorites.some((f) => f._id === p._id)
                                ? `${p.title} quitado de favoritos`
                                : `${p.title} agregado a favoritos ❤️`,
                              { autoClose: 1200 }
                            );
                          }}
                          className="p-1.5 rounded-xl border border-gray-100 bg-gray-50 hover:bg-red-50 transition shrink-0"
                        >
                          <Heart size={11} className={isFav ? "text-red-500 fill-red-500" : "text-gray-300"} />
                        </button>
                        <button
                          onClick={() => setQuickView({ product: p, isOpen, closedMessage: closed })}
                          className="p-1.5 rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 transition shrink-0"
                          title="Ver descripción"
                        >
                          <Eye size={11} className="text-gray-400" />
                        </button>
                        <CartQtyControl
                          product={p}
                          isOpen={isOpen}
                          size="sm"
                          addClassName="bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-sm hover:shadow-md"
                        />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          ) : loading ? (
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                  <div className="h-36 bg-gray-100" />
                  <div className="p-3 space-y-2">
                    <div className="h-3 bg-gray-100 rounded-full w-4/5" />
                    <div className="h-4 bg-gray-100 rounded-full w-2/5" />
                    <div className="h-7 bg-gray-100 rounded-xl mt-3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-24">
              <div className="w-20 h-20 bg-orange-50 rounded-3xl flex items-center justify-center mx-auto mb-4">
                <PackageSearch size={36} className="text-orange-200" />
              </div>
              <p className="text-sm font-bold text-gray-500">No hay productos en oferta</p>
              <p className="text-xs text-gray-400 mt-1">Vuelve más tarde para ver descuentos</p>
              <button
                onClick={() => navigate("/")}
                className="mt-5 px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold rounded-full shadow hover:shadow-md transition"
              >
                Volver al inicio
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Infinite scroll trigger */}
        <div ref={bottomRef} className="w-full h-16 mt-4 flex justify-center items-center">
          {loading && sortedProducts.length > 0 && (
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
              Cargando más...
            </div>
          )}
          {!loading && !hasMore && sortedProducts.length > 0 && (
            <span className="text-gray-400 text-xs font-medium">✓ Has visto todos los descuentos</span>
          )}
        </div>
      </div>

      <MobileBottomBar />

      {quickView && (
        <ProductQuickViewModal
          product={quickView.product}
          isOpen={quickView.isOpen}
          closedMessage={quickView.closedMessage}
          onClose={() => setQuickView(null)}
        />
      )}
    </div>
  );
}
